import { Box, Button, Skeleton, Tooltip, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { DeleteDialog } from 'ui/DeleteDialog';
import { getShopsList, getShopsListIsLoad } from 'redux/reducers/shops/selectors';
import {
	deleteShopRequest,
	getShopsRequest,
	migrateShopRequest,
	fetchShopMigrationStatusRequest,
	restartShopMigrationRequest,
} from 'redux/reducers/shops/reducer';
import { TShop } from 'redux/reducers/shops/types';
import { EasTableWithoutFilter } from 'ui/EasTableWithoutFilter';
import { ConfirmationDialog } from 'ui/ConfirmationDialog';
import { tableStructure } from './tableStructure';
import { AddShopDialog } from './AddShopDialog';
import { MapFieldsDialog } from './MapFieldsDialog';
import { ProgressComponent } from './ProgressComponent';

const CustomComponent = ({ item }: { item: TShop }) => {
	if (item.state === 'Failed') {
		return (
			<Tooltip
				title={item.migrationErrorMessage ?? 'Failed'}
				sx={{
					backgroundColor: 'warning.light',
					'&& .MuiTooltip-tooltip': {
						backgroundColor: 'warning.light',
					},
					'&& .MuiTooltip-arrow': {
						color: 'warning.light',
					},
				}}
			>
				<Typography
					color="error"
					sx={{
						fontSize: '0.8rem',
						whiteSpace: 'nowrap',
						textOverflow: 'ellipsis',
						overflow: 'hidden',
						maxWidth: '300px',
					}}
				>
					{item.migrationErrorMessage ?? 'Failed'}
				</Typography>
			</Tooltip>
		);
	}
	return <ProgressComponent item={item} />;
};

export const Shops: FC = () => {
	console.log('========Shops========');
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);
	const { page } = queryString.parse(search);
	const shops = useSelector(getShopsList);
	const shopsListIsLoad = useSelector(getShopsListIsLoad);
	const [openAddShopDialog, setOpenAddShopDialog] = useState(false);
	const [openMapFieldsDialog, setOpenMapFieldsDialog] = useState(false);
	const [openStartParsingDialog, setOpenStartParsingDialog] = useState(false);
	const [openRestartMigrationDialog, setOpenRestartMigrationDialog] = useState(false);
	// const navigateParamsWithoutPage = tableStructure.reduce((acc, item) => {
	// 	if (item.type === ETableStructureType.DATE && navigateParams[item.selector]) {
	// 		const dates = DATES_ARRAY.find((i) => i.id === navigateParams[item.selector]);
	// 		if (dates?.from) {
	// 			acc.push({
	// 				fieldName: item.selector,
	// 				value: dates.from,
	// 				operator: '>=',
	// 			});
	// 		}
	// 		if (dates?.to) {
	// 			acc.push({
	// 				fieldName: item.selector,
	// 				value: dates.to,
	// 				operator: '<=',
	// 			});
	// 		}
	// 		return acc;
	// 	}

	// 	if (navigateParams[item.selector])
	// 		acc.push({
	// 			fieldName: item.selector,
	// 			value: navigateParams[item.selector] as string,
	// 			operator: '=',
	// 		});
	// 	return acc;
	// }, [] as TFilters);

	useEffect(() => {
		dispatch(getShopsRequest());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, search]);

	const [openUploadSalesReport, setOpenUploadSalesReport] = useState(false);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [shopId, setShopId] = useState<number | null>(null);
	const handleDeleteShop = (id: number) => {
		setOpenDeleteDialog(false);
		setShopId(id);
		dispatch(deleteShopRequest({ shopId: id }));
	};

	// useEffect(() => {
	// 	dispatch(getUploadedSalesReportsRequest({}));
	// }, [dispatch]);

	const handleAction = (id: number, typeOfDialog: string) => {
		if (typeOfDialog === 'Delete Shop') {
			setOpenDeleteDialog(true);
			setShopId(id);
		} else if (typeOfDialog === 'View Goods') {
			navigate(`/shops/${id}/goods/`);
		} else if (typeOfDialog === 'Start Parsing') {
			setOpenStartParsingDialog(true);
			setShopId(id);
		} else if (typeOfDialog === 'Map Fields') {
			setOpenMapFieldsDialog(true);
			setShopId(id);
		} else if (typeOfDialog === 'Restart products migration') {
			setOpenRestartMigrationDialog(true);
			setShopId(id);
		}
	};

	const handleLink = (id: number) => {
		if (!id) return;
		navigate(`/shops/${id}/goods/`);
	};

	const handleStartParsing = (id: number) => {
		if (!id) return;
		dispatch(migrateShopRequest({ shopId: id }));
		setOpenStartParsingDialog(false);
	};

	const handleRestartMigration = (id: number) => {
		if (!id) return;
		dispatch(restartShopMigrationRequest({ shopId: id }));
		setOpenRestartMigrationDialog(false);
	};

	const handleProgress = (id: number) => {
		if (!id) return;
		dispatch(fetchShopMigrationStatusRequest({ shopId: id }));
	};

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<EasTableWithoutFilter<TShop>
				tableStructure={tableStructure}
				items={shops}
				itemsIsLoading={shopsListIsLoad}
				handleAction={handleAction}
				handleLink={handleLink}
				skeletonRows={new Array(10).fill(0)}
				CustomComponent={CustomComponent}
				pagination={false}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography
						variant="h1"
						color="primary"
						sx={{
							fontSize: '1.1rem',
							fontWeight: 'bold',
							textAlign: 'center',
							ml: 1,
						}}
					>
						Shops
					</Typography>
					<Button
						variant="contained"
						color="primary"
						onClick={() => setOpenAddShopDialog(true)}
						size="small"
					>
						Add Shop
					</Button>
				</Box>
			</EasTableWithoutFilter>
			<DeleteDialog
				open={openDeleteDialog}
				setClose={() => setOpenDeleteDialog(false)}
				handleDelete={() => handleDeleteShop(shopId ?? 0)}
				text="Are you sure you want to delete this shop?"
			/>
			<AddShopDialog open={openAddShopDialog} onClose={() => setOpenAddShopDialog(false)} />
			<MapFieldsDialog
				open={openMapFieldsDialog}
				onClose={() => setOpenMapFieldsDialog(false)}
				shopId={shopId ?? null}
			/>
			<ConfirmationDialog
				open={openStartParsingDialog}
				setClose={() => setOpenStartParsingDialog(false)}
				handleConfirm={() => handleStartParsing(shopId ?? 0)}
				text="Are you sure you want to start parsing?"
			/>
			<ConfirmationDialog
				open={openRestartMigrationDialog}
				setClose={() => setOpenRestartMigrationDialog(false)}
				handleConfirm={() => handleRestartMigration(shopId ?? 0)}
				text="Are you sure you want to restart products migration?"
			/>
		</Box>
	);
};
